import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { PageHeader } from "@/components/app/shell/page-header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { optionalOneOf, searchPatch } from "@/lib/search";
import { useCan } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { MedicaidRateEntriesPanel } from "./-components/care/medicaid-rate-entries-panel";
import { MedicaidRateSchedulesPanel } from "./-components/care/medicaid-rate-schedules-panel";
import { RugLevelsPanel } from "./-components/care/rug-levels-panel";
import { OneBuildingCard } from "@/components/app/workspace/one-building-card";

const TABS = ["entries", "rug-levels"] as const;

export const Route = createFileRoute("/_authenticated/_administration/settings/medicaid-rates")({
  validateSearch: (search: Record<string, unknown>): { tab?: (typeof TABS)[number] } => ({
    tab: optionalOneOf(TABS, search.tab),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "medicaid-rates"),
  component: MedicaidRatesSettings,
});

/** Schedules belong to one building, so creating anything needs a building chosen, not "every
 * building". */
function MedicaidRatesSettings() {
  const { facilityId } = useFacility();
  const writableFacilityId = facilityId === "all" ? null : facilityId;
  const canManage = useCan("financial:manage");
  const { tab: tabParam } = Route.useSearch();
  const tab = tabParam ?? "schedules";
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const setTab = (next: string) => patch({ tab: optionalOneOf(TABS, next) });
  const [scheduleId, setScheduleId] = useState<string | null>(null);

  const facilities = useQuery(
    orpc.administration.facilities.list.queryOptions({ input: { includeInactive: true } }),
  );
  const rows = facilities.data;
  const facilityName = useCallback(
    (id: string | null) => {
      if (!id) return "Every building";
      return rows?.find((facility) => facility.id === id)?.name ?? "Unknown building";
    },
    [rows],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Medicaid rates"
        meta="What Medicaid pays this building, for which code, and between which dates."
      />

      {canManage && !writableFacilityId ? (
        <OneBuildingCard
          prompt={
            "Every building's rates are listed below. To add a schedule, a rate or a RUG level, choose its building."
          }
        />
      ) : null}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="schedules">Rate schedules</TabsTrigger>
          <TabsTrigger value="entries">Rates</TabsTrigger>
          <TabsTrigger value="rug-levels">RUG levels</TabsTrigger>
        </TabsList>

        <TabsContent value="schedules">
          <MedicaidRateSchedulesPanel
            facilityId={facilityId}
            writableFacilityId={writableFacilityId}
            canManage={canManage}
            facilityName={facilityName}
            onOpen={(schedule) => {
              setScheduleId(schedule.id);
              setTab("entries");
            }}
          />
        </TabsContent>

        <TabsContent value="entries">
          <MedicaidRateEntriesPanel
            scheduleId={scheduleId}
            canManage={canManage}
            onPickSchedule={() => setTab("schedules")}
          />
        </TabsContent>

        <TabsContent value="rug-levels">
          <RugLevelsPanel
            facilityId={facilityId}
            writableFacilityId={writableFacilityId}
            canManage={canManage}
            facilityName={facilityName}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
